import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import ProductCard from "@/components/ProductCard";
import { SlidersHorizontal } from "lucide-react";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const brand = params.get("brand") || "";
  const sort = params.get("sort") || "";
  const min = params.get("min") || "";
  const max = params.get("max") || "";

  useEffect(() => { api.get("/brands").then((r) => setBrands(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    if (brand) p.set("brand", brand);
    if (min) p.set("min_price", min);
    if (max) p.set("max_price", max);
    if (sort) p.set("sort", sort);
    api.get(`/products?${p.toString()}`).then((r) => { setProducts(r.data); setLoading(false); });
  }, [q, category, brand, sort, min, max]);

  const update = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  const clear = () => setParams({});

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="mb-8">
        <div className="text-sm uppercase tracking-widest text-blue-700 font-semibold mb-2">Shop</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-950">{category || "All Products"}</h1>
        {q && <p className="mt-2 text-slate-600">Results for “<span className="font-medium">{q}</span>”</p>}
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-8">
        <aside className="lg:sticky lg:top-24 lg:self-start bg-white border border-slate-200 rounded-2xl p-6 space-y-6 h-fit" data-testid="filters-panel">
          <div className="flex items-center gap-2 font-heading font-semibold text-slate-900">
            <SlidersHorizontal className="w-4 h-4" /> Filters
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Category</div>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
              <button onClick={() => update("category", "")} className={`block w-full text-left text-sm px-2 py-1.5 rounded-lg ${!category ? "bg-blue-50 text-blue-900 font-medium" : "text-slate-600 hover:bg-slate-50"}`}>All</button>
              {CATEGORIES.map((c) => (
                <button key={c.slug} onClick={() => update("category", c.slug)} className={`block w-full text-left text-sm px-2 py-1.5 rounded-lg ${category === c.slug ? "bg-blue-50 text-blue-900 font-medium" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`filter-cat-${c.slug}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Brand</div>
            <select value={brand} onChange={(e) => update("brand", e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" data-testid="filter-brand">
              <option value="">All Brands</option>
              {brands.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Price (₹)</div>
            <div className="flex gap-2">
              <input type="number" placeholder="Min" value={min} onChange={(e) => update("min", e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" data-testid="filter-min" />
              <input type="number" placeholder="Max" value={max} onChange={(e) => update("max", e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" data-testid="filter-max" />
            </div>
          </div>

          <button onClick={clear} className="w-full text-sm text-slate-600 hover:text-blue-900 border border-slate-200 rounded-xl py-2" data-testid="filter-clear">Clear all</button>
        </aside>

        <div>
          <div className="flex items-center justify-between mb-6">
            <div className="text-sm text-slate-500">{products.length} products</div>
            <select value={sort} onChange={(e) => update("sort", e.target.value)} className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white" data-testid="filter-sort">
              <option value="">Sort: Recommended</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="newest">Newest</option>
              <option value="rating">Top Rated</option>
            </select>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {Array.from({length:6}).map((_,i)=><div key={i} className="aspect-square bg-slate-100 rounded-2xl animate-pulse"></div>)}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24 text-slate-500 border border-slate-200 rounded-2xl">No products match your filters.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
